import React from "react";
import ReactDOM from "react-dom/client";
import i18n from "./i18n"; // must run before App (and services/general) to set localStorage from URL
import "./input.css";
import "./styles/contracts.css";
import "./App.css";
import "./sidebar.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import {
  createBrowserRouter,
  RouterProvider,
  useRouteError,
} from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import ChunkLoadErrorBoundary from "./components/shared/ChunkLoadErrorBoundary";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import "./posthog";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 2 * 60 * 1000, // 2 minutes - general default
      gcTime: 10 * 60 * 1000, // 10 minutes - keep unused cache longer
    },
  },
});

let lng = localStorage.getItem("default-lg");

// A data router's routes catch render errors with their own boundary; rethrowing hands the
// error on to `ChunkLoadErrorBoundary`, as it did under `BrowserRouter`.
const RethrowRouteError = () => {
  throw useRouteError();
};

/**
 * A data router rather than `BrowserRouter` only because `useBlocker` (the leave-page guard,
 * `hooks/useLeavePageGuard`) works under nothing else. The whole app is one catch-all route,
 * so `App` and `components/routes` keep declaring their routes with `<Routes>` as before.
 * The app has no loaders, actions or fetchers; the v7 flags only opt in early and silence
 * the deprecation warnings.
 */
const router = createBrowserRouter(
  [
    {
      path: "*",
      element: (
        <I18nextProvider i18n={i18n}>
          <div className={lng}>
            <App />
          </div>
        </I18nextProvider>
      ),
      errorElement: <RethrowRouteError />,
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <ChunkLoadErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </ChunkLoadErrorBoundary>
);

// If you want to start measuring performance in your app, pass a function
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
